/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { AnyAction } from 'redux';

import {
    Diagnostics,
    Operations,
    Payload,
    SegmentTransport,
    VU,
} from '@tivocorp/tivo-webapp-loggers';
import { ServiceEndpoints } from 'client-code/sls-service-endpoints';

import { KAVERI_GLOBAL_CATCH_NAME } from '../../config';
import { Branding } from '../../core/branding/Branding';
import { DeviceFeatures } from '../../core/device/DeviceFeatures';
import { DeviceModel } from '../../core/device/DeviceModel';
import { NetworkInterceptor } from '../../core/device/NetworkInterceptor';
import { GlobalCatch } from '../../core/error';
import { Locale } from '../../core/locale/Locale';
import { RestartReasonsOperationsLogger, SageLogger, VuLogger } from '../../core/logging';
import { Log } from '../../core/logging/Log';
import { QoEAppLogger } from '../../core/logging/qoe/QoEAppLogger';
import { Storage } from '../../core/storage/Storage';
import { UniqueIdentifier } from '../../core/utils/UniqueIdentifier';
import { resetAntiburnTimeout } from '../antiburnosd/antiburnosd.reducers';
import { languageNotSupportedError } from '../error/erroroverlays.reducers';
import { launchHomeScreen } from '../signin/postsignin.reducers';
import { prepareForAppLaunch } from '../signin/presignin.reducers';
import { initiateSignin } from '../signin/signin.reducers';
import {
    appStartAction,
    overridePlaybackParamsAction,
    updateCommonInactivityTimeStampAction,
} from './appl.actions';
import {
    APP_START,
    IS_APPLICATION_READY,
    OVERRIDE_PLAYBACK_PARAMS,
    SET_PARTNER_BRANDING_IMAGE_URL,
    SET_SLS_GLOBAL_ENDPOINTS,
    UPDATE_COMMON_INACTIVITY_TIMESTAMP,
} from './appl.types';
import { AppUtils } from '../../core/AppUtils';

const TAG = 'appl';

const initialState: {
    commonInactivityTimeStamp: number;
    isOverrideEnable: boolean;
    overrideDetails: any;
    slsGlobalEndpoints: ServiceEndpoints[];
    isApplicationReady: boolean;
    partnerBrandingImageUrl: string;
} = {
    commonInactivityTimeStamp: Date.now(),
    isOverrideEnable: false,
    overrideDetails: {},
    slsGlobalEndpoints: [],
    isApplicationReady: false,
    partnerBrandingImageUrl: '',
};

const applReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case APP_START:
            return state;


        case SET_SLS_GLOBAL_ENDPOINTS:
            return {
                ...state,
                slsGlobalEndpoints: payload.slsGlobalEndpoints,
            };
        case IS_APPLICATION_READY:
            return {
                ...state,
                isApplicationReady: payload.isApplicationReady,
            };

        case SET_PARTNER_BRANDING_IMAGE_URL:
            return {
                ...state,
                partnerBrandingImageUrl: payload.partnerBrandingImageUrl,
            };

        case UPDATE_COMMON_INACTIVITY_TIMESTAMP:
            return {
                ...state,
                commonInactivityTimeStamp: payload.commonInactivityTimeStamp,
            };

        default:
            return state;
    }
};


const initializeOperationsLogging = () => {
    const globalParam = DeviceModel.getGlobalParams();

    AppUtils.filterUnknownOrEmpty(globalParam);

    const globalPayload: Payload = new Payload(globalParam);
    globalPayload.add('sessionId', UniqueIdentifier.getSessionId());
    Operations.setGlobals(globalPayload);
    Operations.noConsole();
    Operations.getInstance().setTransport(SageLogger.getInstance().getOperationsTransport());
};

export const appStart = () => async (dispatch: Function) => {
    // Initialize Sage Logger with the default configure with app environment.
    SageLogger.getInstance().configure({ environment: DeviceModel.getAppEnvironment() });
    Diagnostics.getInstance().setTransport(SageLogger.getInstance().getDiagnosticsTransport());

    // Initialize Operations Logger with the default configure with app environment.
    initializeOperationsLogging();

    NetworkInterceptor.logging(DeviceFeatures.isServiceApiLoggingEnabled());
    // Application execution session identifier
    QoEAppLogger.createAppSessionId();

    Diagnostics.noConsole();
    Diagnostics.info(TAG, 'Application start.');
    Log.i(TAG, 'Application start.');
    dispatch(appStartAction());

    Diagnostics.info(TAG, 'Device global parameters:', DeviceModel.getGlobalParams());
    Log.i(TAG, 'Device global parameters:', DeviceModel.getGlobalParams());
    Log.i(TAG, 'Device Total Memory:', DeviceModel.getTVProperties().getDeviceTotalMemory());

    if (!DeviceModel.getTVProperties().hasSupportedLanguage()) {
        dispatch(languageNotSupportedError());
        return;
    }

    Locale.setLocale(
        DeviceModel.getTVProperties().getLocale(),
        DeviceModel.getTiVoSettings().getMsoName(),
        DeviceModel.getTVProperties().getPlatformName(),
    );


    dispatch(appRunning());
};
const initializeVuLogging = () => {
    const globalParam = {
        app: {
            name: DeviceModel.getAppName(),
            version: DeviceModel.getAppVersionName(),
        },
        applicationSessionId: UniqueIdentifier.uuid4(),
        device: {
            id: DeviceModel.getTVProperties().getMacAddress(),
            type: DeviceModel.getTiVoSettings().getDeviceType(),
            adTrackingEnabled: false,
        },
        locale: DeviceModel.getTVProperties().getLocale().replace('_', '-'),
        msoName: DeviceModel.getTiVoSettings().getMsoName(),
        timezone: DeviceModel.getTVProperties().getTimezone(),
    };
    const globalPayload: Payload = new Payload(globalParam);
    VuLogger.setGlobalParams(globalPayload);
    VU.noConsole();
    VU.getInstance().setTransport(
        new SegmentTransport({
            key: DeviceModel.getTiVoSettings().getSegmentTransportKey(),
        }),
    );
};

const initializeApplicationStartupLogs = () => {
    const globalPayload: Payload = new Payload(DeviceModel.getGlobalParams());

    Diagnostics.info(TAG, 'Application startup completed.');
    Diagnostics.setGlobals(globalPayload);
};

const logRestartReason = () => {
    const globalCatch = GlobalCatch.getInstance(KAVERI_GLOBAL_CATCH_NAME);
    const crashReferenceTime =
        globalCatch.hasCrashed() && globalCatch.getLatestCatch()?.referenceTime;
    RestartReasonsOperationsLogger.getInstance().initialize(
        DeviceModel.getAppVersionName(),
        DeviceModel.getTiVoSettings().getShellVersion(),
        crashReferenceTime,
    );
    RestartReasonsOperationsLogger.getInstance().logRestartReason();
};

export const appRunning = () => async (dispatch: Function) => {
    Branding.setFallbackBrandings(
        DeviceModel.getTVProperties().getLocale(),
        DeviceModel.getTiVoSettings().getMsoName(),
    );
    initializeVuLogging();
    initializeApplicationStartupLogs();
    logRestartReason();
    VuLogger.logAppEntryEvent();
    await dispatch(prepareForAppLaunch());
    await dispatch(initiateSignin());
    await dispatch(launchHomeScreen());
    dispatch(resetAntiburnTimeout());
};

export default applReducer;
